import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  message,
  Space,
  Popconfirm,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import { usePermissions } from '../../../context/permissionsContext';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

const formatPesosAr = (value) => {
  const n = typeof value === 'number' ? value : parseFloat(value);
  const num = Number.isFinite(n) ? n : 0;
  return `$${num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const MESES_NOMBRE = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const OPCIONES_TIPO_SUELDO = [
  { value: 'Sueldo', label: 'Sueldo' },
  { value: 'Extra', label: 'Extra' },
  { value: 'Adelanto', label: 'Adelanto' },
  { value: 'Aguinaldo', label: 'Aguinaldo' },
];

const etiquetaPeriodo = (yyyyMm) => {
  if (!yyyyMm || typeof yyyyMm !== 'string') return '-';
  const m = /^(\d{4})-(\d{2})$/.exec(yyyyMm.trim());
  if (!m) return yyyyMm;
  const mes = parseInt(m[2], 10);
  if (mes < 1 || mes > 12) return yyyyMm;
  return `${MESES_NOMBRE[mes - 1]}-${m[1]}`;
};

const Sueldos = () => {
  const { hasPermission } = usePermissions();
  const [sueldos, setSueldos] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSueldo, setEditingSueldo] = useState(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [fechaRango, setFechaRango] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [filtroEmpleadoId, setFiltroEmpleadoId] = useState(null);
  const [filtroPeriodo, setFiltroPeriodo] = useState(null);

  const opcionesPeriodoAnioActual = useMemo(() => {
    const year = dayjs().year();
    return MESES_NOMBRE.map((nombreMes, i) => {
      const mm = String(i + 1).padStart(2, '0');
      return {
        value: `${year}-${mm}`,
        label: `${nombreMes}-${year}`,
      };
    });
  }, []);

  const loadEmpleados = async () => {
    try {
      const response = await apiClient.get('/gestion/sueldos/recursos/empleados');
      if (response.ok) {
        const data = await response.json();
        setEmpleados(data || []);
      }
    } catch (error) {
      console.error('Error al cargar empleados:', error);
    }
  };

  const loadFormasPago = async () => {
    try {
      const response = await apiClient.get('/gestion/sueldos/recursos/formas-pago');
      if (response.ok) {
        const data = await response.json();
        setFormasPago(data || []);
      }
    } catch (error) {
      console.error('Error al cargar formas de pago:', error);
    }
  };

  const loadSueldos = async (rangoOverride = null) => {
    setLoading(true);
    try {
      const rango = rangoOverride != null ? rangoOverride : fechaRango;
      const params = new URLSearchParams();
      if (rango && rango[0]) params.append('fecha_desde', dayjs(rango[0]).format('YYYY-MM-DD'));
      if (rango && rango[1]) params.append('fecha_hasta', dayjs(rango[1]).format('YYYY-MM-DD'));
      if (filtroEmpleadoId) params.append('empleado_id', String(filtroEmpleadoId));
      if (filtroPeriodo) params.append('periodo', filtroPeriodo);

      const response = await apiClient.get(`/gestion/sueldos?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setSueldos(data || []);
      } else {
        message.error('Error al cargar sueldos');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar sueldos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmpleados();
    loadFormasPago();
    loadSueldos();
  }, []);

  useEffect(() => {
    if (fechaRango && fechaRango[0] && fechaRango[1]) {
      loadSueldos();
    }
  }, [fechaRango, filtroEmpleadoId, filtroPeriodo]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleOpenModal = (sueldo = null) => {
    setEditingSueldo(sueldo);
    if (sueldo) {
      form.setFieldsValue({
        empleado_id: sueldo.sue_empleado_id,
        fecha: dayjs(sueldo.sue_fecha),
        periodo: sueldo.sue_periodo || dayjs(sueldo.sue_fecha).format('YYYY-MM'),
        tipo: sueldo.sue_tipo || 'Sueldo',
        metodo_pago: sueldo.sue_metodo_pago,
        importe: parseFloat(sueldo.sue_importe),
        observaciones: sueldo.sue_observaciones,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        fecha: dayjs(),
        periodo: dayjs().format('YYYY-MM'),
        tipo: 'Sueldo',
        metodo_pago: formasPago[0]?.fop_nombre || undefined,
      });
    }
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        empleado_id: values.empleado_id,
        fecha: values.fecha.format('YYYY-MM-DD'),
        periodo: values.periodo,
        tipo: values.tipo,
        metodo_pago: values.metodo_pago,
        importe: values.importe,
        observaciones: values.observaciones || null,
      };

      if (editingSueldo) {
        const response = await apiClient.put(`/gestion/sueldos/${editingSueldo.sue_id}`, payload);
        if (response.ok) {
          message.success('Sueldo actualizado correctamente');
          setModalVisible(false);
          loadSueldos();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar sueldo');
        }
      } else {
        const response = await apiClient.post('/gestion/sueldos', payload);
        if (response.ok) {
          message.success('Sueldo registrado correctamente');
          setModalVisible(false);
          loadSueldos();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al registrar sueldo');
        }
      }
    } catch (error) {
      if (error.errorFields) return;
      console.error('Error:', error);
      message.error('Error al guardar el sueldo');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/gestion/sueldos/${id}`);
      if (response.ok) {
        message.success('Sueldo eliminado correctamente');
        loadSueldos();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar sueldo');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar sueldo');
    }
  };

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'sue_fecha',
      key: 'sue_fecha',
      render: (fecha) => dayjs(fecha).format('DD/MM/YYYY'),
    },
    {
      title: 'Empleado',
      dataIndex: 'sue_empleado_nombre',
      key: 'sue_empleado_nombre',
    },
    {
      title: 'Período',
      dataIndex: 'sue_periodo',
      key: 'sue_periodo',
      render: (p) => etiquetaPeriodo(p),
    },
    {
      title: 'Tipo',
      dataIndex: 'sue_tipo',
      key: 'sue_tipo',
      render: (t) => (t ? <Tag color="default">{t}</Tag> : '-'),
    },
    {
      title: 'Método',
      dataIndex: 'sue_metodo_pago',
      key: 'sue_metodo_pago',
      render: (metodo) => <Tag color="blue">{metodo}</Tag>,
    },
    {
      title: 'Importe',
      dataIndex: 'sue_importe',
      key: 'sue_importe',
      align: 'right',
      render: (importe) => formatPesosAr(importe),
    },
    {
      title: 'Observación',
      dataIndex: 'sue_observaciones',
      key: 'sue_observaciones',
      render: (value) => value || '-',
    },
    ...((hasPermission('sueldos', 'editar') || hasPermission('sueldos', 'eliminar'))
      ? [{
          title: 'Acciones',
          key: 'acciones',
          width: 180,
          render: (_, record) => (
            <Space>
              {hasPermission('sueldos', 'editar') && (
                <Button type="link" icon={<EditOutlined />} onClick={() => handleOpenModal(record)}>
                  Editar
                </Button>
              )}
              {hasPermission('sueldos', 'eliminar') && (
                <Popconfirm
                  title="¿Eliminar este sueldo?"
                  onConfirm={() => handleDelete(record.sue_id)}
                  okText="Sí"
                  cancelText="No"
                >
                  <Button type="link" danger icon={<DeleteOutlined />}>
                    Eliminar
                  </Button>
                </Popconfirm>
              )}
            </Space>
          ),
        }]
      : []),
  ];

  const totalImporteFiltrado = useMemo(
    () =>
      sueldos.reduce((sum, s) => {
        const n = parseFloat(s.sue_importe);
        return sum + (Number.isFinite(n) ? n : 0);
      }, 0),
    [sueldos]
  );

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>
            Gestión de Sueldos
          </h1>
          <Space wrap style={{ marginBottom: 16 }}>
            <Button icon={<ReloadOutlined />} onClick={loadSueldos} loading={loading}>
              {isMobile ? null : 'Actualizar'}
            </Button>
            {hasPermission('sueldos', 'crear') && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal(null)}>
                Nuevo Sueldo
              </Button>
            )}
          </Space>

          <div
            style={{
              marginBottom: 16,
              padding: 16,
              backgroundColor: '#2d2d2d',
              borderRadius: 8,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 12,
              alignItems: isMobile ? 'stretch' : 'flex-end',
            }}
          >
            <div style={{ flex: 1, minWidth: isMobile ? '100%' : '280px' }}>
              <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                Rango de fechas
              </label>
              <RangePicker
                value={fechaRango}
                onChange={(dates) => setFechaRango(dates)}
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ flex: isMobile ? 1 : '0 1 220px', minWidth: isMobile ? '100%' : 200 }}>
              <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                Período
              </label>
              <Select
                placeholder="Todos los períodos"
                value={filtroPeriodo}
                onChange={(value) => setFiltroPeriodo(value ?? null)}
                allowClear
                showSearch
                style={{ width: '100%' }}
                optionFilterProp="label"
                options={opcionesPeriodoAnioActual.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
              />
            </div>
            <div
              style={{
                flex: 1,
                minWidth: isMobile ? '100%' : '240px',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 12,
                alignItems: isMobile ? 'stretch' : 'flex-end',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                  Empleado
                </label>
                <Select
                  placeholder="Todos los empleados"
                  value={filtroEmpleadoId}
                  onChange={(value) => setFiltroEmpleadoId(value)}
                  allowClear
                  showSearch
                  style={{ width: '100%' }}
                  filterOption={(input, option) =>
                    String(option?.children || '').toLowerCase().includes((input || '').toLowerCase())
                  }
                >
                  {empleados.map((emp) => (
                    <Select.Option key={emp.emp_id} value={emp.emp_id}>
                      {emp.emp_nombre}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div
                style={{
                  flexShrink: 0,
                  padding: isMobile ? '8px 0 0' : '0 0 4px',
                  textAlign: isMobile ? 'left' : 'right',
                  minWidth: isMobile ? '100%' : 160,
                }}
              >
                <div style={{ fontSize: 12, color: '#b0b0b0', marginBottom: 4 }}>Total importes</div>
                <div
                  style={{
                    fontSize: isMobile ? 18 : 20,
                    fontWeight: 700,
                    color: '#d8ff3e',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatPesosAr(totalImporteFiltrado)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={sueldos}
          loading={loading}
          rowKey="sue_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} sueldos`,
          }}
        />

        <Modal
          title={editingSueldo ? 'Editar Sueldo' : 'Nuevo Sueldo'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          okText="Guardar"
          cancelText="Cancelar"
          width={700}
        >
          <Form form={form} layout="vertical">
            <div style={{ display: 'flex', gap: 16 }}>
              <Form.Item
                name="empleado_id"
                label="Empleado"
                rules={[{ required: true, message: 'Seleccione un empleado' }]}
                style={{ flex: 1 }}
              >
                <Select
                  placeholder="Seleccione empleado"
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children || '').toLowerCase().includes((input || '').toLowerCase())
                  }
                >
                  {empleados.map((emp) => (
                    <Select.Option key={emp.emp_id} value={emp.emp_id}>
                      {emp.emp_nombre}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="fecha"
                label="Fecha"
                rules={[{ required: true, message: 'Seleccione la fecha' }]}
                style={{ flex: 1 }}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </div>

            <div style={{ display: 'flex', gap: 16 }}>
              <Form.Item
                name="periodo"
                label="Período"
                rules={[{ required: true, message: 'Seleccione el período' }]}
                style={{ flex: 1 }}
              >
                <Select
                  placeholder="Mes-Año"
                  showSearch
                  optionFilterProp="label"
                  options={opcionesPeriodoAnioActual.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
              </Form.Item>

              <Form.Item
                name="tipo"
                label="Tipo"
                rules={[{ required: true, message: 'Seleccione el tipo' }]}
                style={{ flex: 1 }}
              >
                <Select
                  placeholder="Tipo"
                  options={OPCIONES_TIPO_SUELDO}
                />
              </Form.Item>
            </div>

            <div style={{ display: 'flex', gap: 16 }}>
              <Form.Item
                name="metodo_pago"
                label="Método de pago"
                rules={[{ required: true, message: 'Seleccione método de pago' }]}
                style={{ flex: 1 }}
              >
                <Select
                  placeholder="Seleccione método de pago"
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children || '').toLowerCase().includes((input || '').toLowerCase())
                  }
                >
                  {formasPago.map((fp) => (
                    <Select.Option key={fp.fop_id} value={fp.fop_nombre}>
                      {fp.fop_nombre}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="importe"
                label="Importe"
                rules={[{ required: true, message: 'Ingrese el importe' }]}
                style={{ flex: 1 }}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }}
                  formatter={(value) => {
                    if (value == null || value === '') return '';
                    const parts = String(value).split('.');
                    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                    return `$ ${parts.join(',')}`;
                  }}
                  parser={(value) => {
                    if (!value || value === '$' || (typeof value === 'string' && value.trim() === '')) return 0;
                    let cleaned = String(value).replace(/\$\s?/g, '').trim();
                    if (!cleaned) return 0;
                    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
                    const num = parseFloat(cleaned);
                    return Number.isNaN(num) || num < 0 ? 0 : num;
                  }}
                />
              </Form.Item>
            </div>

            <Form.Item name="observaciones" label="Observación">
              <TextArea rows={3} placeholder="Observaciones (opcional)" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default Sueldos;
